// Turning pdf.js text runs into sentences.
//
// A PDF stores positioned runs of glyphs — it has no notion of a line, a
// paragraph, or a sentence. The rest of the app treats one line as one study
// item (see `parseSentenceLines` in utils.ts), so the reading order has to be
// rebuilt here: runs → visual lines → paragraphs → sentences.
//
// Kept apart from pdf.ts, which is the pdf.js plumbing, because this half is
// pure and is where all the guesswork lives.

/** Structural check: pdf.js mixes TextItem and TextMarkedContent in one array. */
export interface PdfTextItem {
  str: string;
  width: number;
  transform: number[];
  hasEOL: boolean;
}

function isTextItem(it: unknown): it is PdfTextItem {
  return typeof (it as PdfTextItem | undefined)?.str === 'string';
}

/** Shape one page's text runs into sentences, in reading order. */
export function pageToSentences(items: unknown[]): string[] {
  const out: string[] = [];
  for (const paragraph of toParagraphs(toLines(items))) {
    out.push(...splitSentences(paragraph));
  }
  return out;
}

interface Line {
  text: string;
  /** Horizontal extent in PDF user space, used to spot a paragraph's last line. */
  x0: number;
  x1: number;
}

/**
 * Group text runs into visual lines.
 *
 * `hasEOL` is pdf.js's own end-of-line marker and is reliable for ordinary
 * text; the baseline check catches PDFs (and table cells) where it never fires.
 */
export function toLines(items: unknown[]): Line[] {
  const lines: Line[] = [];
  let buf = '';
  let x0 = Infinity;
  let x1 = -Infinity;
  let y: number | null = null;

  const flush = () => {
    const text = tidy(buf);
    if (text) lines.push({ text, x0, x1 });
    buf = '';
    x0 = Infinity;
    x1 = -Infinity;
  };

  for (const raw of items) {
    if (!isTextItem(raw)) continue; // marked-content markers carry no text
    const itemX = raw.transform[4];
    const itemY = raw.transform[5];

    // A baseline shift of more than a couple of points starts a new line even
    // when the previous run did not claim to end one.
    if (y !== null && Math.abs(itemY - y) > 2) flush();
    y = itemY;

    buf += raw.str;
    if (raw.str.trim()) {
      x0 = Math.min(x0, itemX);
      x1 = Math.max(x1, itemX + raw.width);
    }

    if (raw.hasEOL) {
      flush();
      y = null;
    }
  }
  flush();
  return lines;
}

/** Collapse whitespace and drop characters that exist only for layout. */
function tidy(s: string): string {
  return s
    .replace(/[­​-‍﻿]/g, '') // soft hyphen, zero-width marks
    .replace(/\s+/g, ' ')
    .trim();
}

/** A line that is only a page number — it would otherwise be read aloud. */
function isPageFurniture(text: string): boolean {
  return /^\d{1,4}$/.test(text) || /^(page|p\.)\s*\d{1,4}$/i.test(text);
}

/**
 * Join lines back into paragraphs.
 *
 * PDFs wrap mid-sentence, so the line breaks have to be undone. The clue for a
 * real paragraph break is a short last line: body text runs to the same right
 * edge, so a line stopping well before it is the end of something.
 */
export function toParagraphs(lines: Line[]): string[] {
  const body = lines.filter((l) => !isPageFurniture(l.text));
  if (!body.length) return [];

  const fullWidth = Math.max(...body.map((l) => l.x1 - l.x0));
  const shortEnough = fullWidth * 0.8;

  const paragraphs: string[] = [];
  let buf = '';

  for (const line of body) {
    if (!buf) {
      buf = line.text;
    } else if (/[‐-—-]$/.test(buf)) {
      buf = buf.slice(0, -1) + line.text; // rejoin a word split across lines
    } else {
      buf += ' ' + line.text;
    }
    if (line.x1 - line.x0 < shortEnough) {
      paragraphs.push(buf);
      buf = '';
    }
  }
  if (buf) paragraphs.push(buf);
  return paragraphs;
}

// Trailing tokens that end in a period without ending a sentence. The leading
// `[A-Z]` alternative covers initials ("J. R. R. Tolkien").
const ABBREVIATION =
  /(?:^|[\s("'])(?:[A-Z]|Mr|Mrs|Ms|Dr|Prof|Rev|Sr|Jr|St|Gen|Col|Lt|Sgt|Fig|No|Vol|pp|Inc|Ltd|Co|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sept?|Oct|Nov|Dec|vs|al|etc|approx|e\.g|i\.e)\.$/;

/** Split a paragraph into sentences, keeping the terminating punctuation. */
export function splitSentences(paragraph: string): string[] {
  const pieces = paragraph.split(/(?<=[.!?…])(?=\s)/);
  const out: string[] = [];

  for (const piece of pieces) {
    const part = piece.trim();
    if (!part) continue;
    const prev = out[out.length - 1];
    // Re-attach when the break was not a real sentence end: an abbreviation
    // before it, or a continuation (lowercase, or punctuation) after it.
    if (prev && (ABBREVIATION.test(prev) || /^[a-z0-9,;:)\]]/.test(part))) {
      out[out.length - 1] = `${prev} ${part}`;
    } else {
      out.push(part);
    }
  }
  return out;
}
